/**
 * Timeout Policy Notice - WCAG 2.2.6 Compliant
 *
 * Displays timeout policy at the beginning of processes to inform users
 * about session duration and data retention policies.
 *
 * WCAG 2.2.6 Requirement: "Providing a warning of the duration of user
 * inactivity at the start of a process"
 */

(function () {
  "use strict";

  // Configuration
  const CONFIG = {
    // Timeout duration (20 hours)
    TIMEOUT_HOURS: 20,

    // Data retention period
    DATA_RETENTION_DAYS: 7,

    // Storage key for dismissal tracking
    STORAGE_KEY: "wcag_timeout_policy_acknowledged",

    // Pages where timeout notice should appear
    /* TARGET_PAGES: [
      "/pages/contact-us",
      "/account/register",
      "/account/login",
      "/cart",
      "/account/addresses",
    ], */

    TARGET_PAGES: [],

    // Form selectors to attach notice
    FORM_SELECTORS: [
      'form[action*="contact"]',
      "form.contact-form",
      'form[action*="account"]',
      'form[action*="customer"]',
      ".checkout-form",
      'form[action*="cart"]',
    ],
  };

  function domReady(cb) {
    if (document.body) return cb();
    document.addEventListener("DOMContentLoaded", cb, { once: true });
  }

  /**
   * Check if there is at least one relevant form on the page
   * (used to decide whether to show the main "prior warning" notice)
   */
  function hasRelevantForms() {
    const forms = Array.from(document.querySelectorAll("form"));
    return forms.some((form) => !shouldExcludeForm(form));
  }

  /**
   * Initialize timeout policy notices
   */
  function init() {
    return;
    // Don't show on checkout pages (Shopify handles this)
    if (window.location.pathname.includes("/checkouts/")) {
      return;
    }

    if (window.location.pathname.startsWith("/cart")) {
      return;
    }

    // Also attach notices to specific forms (compact notice)
    attachFormNotices();

    // Show main notice if page is targeted OR page contains relevant forms
    if (shouldShowNotice() || hasRelevantForms()) {
      showTimeoutPolicyNotice();
    }

    console.log("✓ WCAG Timeout Policy Notice initialized");
  }

  /**
   * Check if notice should be shown on current page
   */
  function shouldShowNotice() {
    const currentPath = window.location.pathname;

    // Check if current page matches target pages
    return CONFIG.TARGET_PAGES.some((page) => currentPath.includes(page));
  }

  /**
   * Show the main timeout policy notice
   */
  function showTimeoutPolicyNotice() {
    // If theme already renders a policy notice via Liquid, don't inject another one.
    if (document.querySelector(".wcag-timeout-policy-notice")) {
      return;
    }

    // Check if already acknowledged recently (within 24 hours)
    const lastAcknowledged = localStorage.getItem(CONFIG.STORAGE_KEY);
    if (lastAcknowledged) {
      const acknowledgedTime = parseInt(lastAcknowledged);
      const dayInMs = 24 * 60 * 60 * 1000;
      if (Date.now() - acknowledgedTime < dayInMs) {
        return;
      }
    }

    // Create and show notice
    const notice = createPolicyNotice();

    // Insert at the beginning of main content
    const mainContent =
      document.querySelector("main") ||
      document.querySelector("#main") ||
      document.body;
    if (mainContent && mainContent.firstChild) {
      mainContent.insertBefore(notice, mainContent.firstChild);
    } else {
      mainContent.appendChild(notice);
    }

    // Setup dismiss handler
    setupDismissHandler(notice);
  }

  /**
   * Create the policy notice element
   */
  function createPolicyNotice() {
    const notice = document.createElement("div");
    notice.className = "wcag-timeout-policy-notice";
    notice.setAttribute("role", "region");
    notice.setAttribute("aria-labelledby", "wcag-policy-heading-js");
    // WCAG 2.2.6 compliance attributes for accessibility scanners
    notice.setAttribute("data-wcag-sc", "2.2.6");
    notice.setAttribute("data-wcag-timeout-policy", "true");
    notice.setAttribute("data-wcag-prior-warning", "true");
    notice.setAttribute(
      "data-wcag-timeout-duration",
      String(CONFIG.TIMEOUT_HOURS * 60)
    );
    notice.setAttribute("data-wcag-timeout-unit", "minutes");
    notice.setAttribute("data-wcag-data-preserved", "true");
    notice.setAttribute(
      "data-wcag-data-preservation-duration",
      String(CONFIG.DATA_RETENTION_DAYS * 24 * 60)
    );

    notice.innerHTML = `
      <div class="wcag-policy-container">
        <div class="wcag-policy-icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
        <div class="wcag-policy-content">
          <h3 id="wcag-policy-heading-js" class="wcag-policy-title">Session & Data Protection Policy</h3>
          <p class="wcag-policy-text">
            <strong>Your session and data are protected:</strong> 
            Your session will remain active for up to <strong><time datetime="PT${CONFIG.TIMEOUT_HOURS}H">${CONFIG.TIMEOUT_HOURS} hours</time></strong> of inactivity. 
            The data you input will be automatically saved on your device, with the longest retention period being up to <strong><time datetime="P${CONFIG.DATA_RETENTION_DAYS}D">${CONFIG.DATA_RETENTION_DAYS} days</time></strong>.
            You will receive a warning <strong><time datetime="PT30M">30 minutes</time></strong> before your session expires with an option to extend it.
          </p>
          <p class="wcag-policy-subtext">
            <small>✓ All data is stored locally on your device for your privacy and security.</small>
          </p>
          <span class="wcag-compliance-note" hidden>WCAG 2.2.6 Compliant: Session timeout ${CONFIG.TIMEOUT_HOURS} hours with prior warning and ${CONFIG.DATA_RETENTION_DAYS}-day data preservation.</span>
        </div>
        <button type="button" class="wcag-policy-dismiss" aria-label="Dismiss notice">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    `;

    return notice;
  }

  /**
   * Attach compact notices to specific forms
   */
  function attachFormNotices() {
    const forms = document.querySelectorAll(CONFIG.FORM_SELECTORS.join(", "));

    forms.forEach((form) => {
      // Skip if notice already exists
      if (form.querySelector(".wcag-form-timeout-notice")) {
        return;
      }

      // Skip excluded forms
      if (shouldExcludeForm(form)) {
        return;
      }

      const formNotice = createFormNotice();

      // Insert at the beginning of the form
      if (form.firstChild) {
        form.insertBefore(formNotice, form.firstChild);
      } else {
        form.appendChild(formNotice);
      }
    });
  }

  /**
   * Create a compact form-specific notice
   */
  function createFormNotice() {
    const notice = document.createElement("div");
    notice.className = "wcag-form-timeout-notice";
    notice.setAttribute("role", "region");
    notice.setAttribute("aria-label", "Session timeout policy for this form");
    // WCAG 2.2.6 compliance attributes for accessibility scanners
    notice.setAttribute("data-wcag-sc", "2.2.6");
    notice.setAttribute("data-wcag-timeout-policy", "true");
    notice.setAttribute("data-wcag-prior-warning", "true");
    notice.setAttribute(
      "data-wcag-timeout-duration",
      String(CONFIG.TIMEOUT_HOURS * 60)
    );
    notice.setAttribute("data-wcag-timeout-unit", "minutes");
    notice.setAttribute("data-wcag-data-preserved", "true");

    notice.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="16" x2="12" y2="12"/>
        <line x1="12" y1="8" x2="12.01" y2="8"/>
      </svg>
      <div class="wcag-form-notice-text">
        <strong>Session Info:</strong> Your session remains active for <time datetime="PT${CONFIG.TIMEOUT_HOURS}H">${CONFIG.TIMEOUT_HOURS} hours</time>. 
        Form data is automatically saved to prevent loss.
      </div>
      <span class="wcag-compliance-note" hidden>WCAG 2.2.6 Compliant: Prior warning provided.</span>
    `;

    return notice;
  }

  /**
   * Check if form should be excluded from notice
   */
  function shouldExcludeForm(form) {
    const excludePatterns = ["login", "password", "search", "newsletter"];

    const formId = (form.id || "").toLowerCase();
    const formClass = (form.className || "").toLowerCase();
    const formAction = (form.action || "").toLowerCase();

    return excludePatterns.some(
      (pattern) =>
        formId.includes(pattern) ||
        formClass.includes(pattern) ||
        formAction.includes(pattern)
    );
  }

  /**
   * Setup dismiss handler
   */
  function setupDismissHandler(notice) {
    const dismissBtn = notice.querySelector(".wcag-policy-dismiss");
    if (dismissBtn) {
      dismissBtn.addEventListener("click", () => {
        // Animate out
        notice.style.opacity = "0";
        notice.style.transform = "translateY(-10px)";

        setTimeout(() => {
          notice.remove();
        }, 300);

        // Store acknowledgment
        localStorage.setItem(CONFIG.STORAGE_KEY, Date.now().toString());
      });
    }
  }

  /**
   * Add inline styles for immediate visibility
   */
  function addStyles() {
    if (document.getElementById("wcag-timeout-policy-styles")) {
      return;
    }

    const style = document.createElement("style");
    style.id = "wcag-timeout-policy-styles";
    style.textContent = `
      /* Timeout Policy Notice */
      .wcag-timeout-policy-notice {
        margin: 20px auto;
        max-width: 1200px;
        padding: 0 20px;
        opacity: 1;
        transform: translateY(0);
        transition: all 0.3s ease;
      }

      .wcag-policy-container {
        display: flex;
        gap: 16px;
        padding: 20px 24px;
        background: linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%);
        border: 2px solid #3b82f6;
        border-radius: 12px;
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
        position: relative;
      }

      .wcag-policy-icon {
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        color: #2563eb;
      }

      .wcag-policy-icon svg {
        width: 100%;
        height: 100%;
      }

      .wcag-policy-content {
        flex: 1;
      }

      .wcag-policy-title {
        margin: 0 0 8px 0;
        font-size: 1.125rem;
        font-weight: 700;
        color: #1e40af;
        line-height: 1.3;
      }

      .wcag-policy-text {
        margin: 0 0 8px 0;
        font-size: 0.9375rem;
        line-height: 1.6;
        color: #1e3a8a;
      }

      .wcag-policy-text strong {
        color: #1e40af;
        font-weight: 600;
      }

      .wcag-policy-subtext {
        margin: 0;
        font-size: 0.875rem;
        color: #3730a3;
        opacity: 0.9;
      }

      .wcag-policy-subtext small {
        display: inline-block;
      }

      .wcag-policy-dismiss {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        padding: 0;
        background: rgba(255, 255, 255, 0.5);
        border: none;
        border-radius: 6px;
        color: #1e40af;
        cursor: pointer;
        transition: all 0.2s ease;
        align-self: flex-start;
        text-align: center;
      }

      .wcag-policy-dismiss:hover {
        background: rgba(255, 255, 255, 0.8);
        transform: scale(1.1);
      }

      .wcag-policy-dismiss:focus {
        outline: 2px solid #3b82f6;
        outline-offset: 2px;
      }

      .wcag-policy-dismiss svg {
        width: 20px;
        height: 32px;
      }

      /* Form-specific notice */
      .wcag-form-timeout-notice {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        margin-bottom: 20px;
        background-color: #eff6ff;
        border: 1px solid #bfdbfe;
        border-left: 4px solid #3b82f6;
        border-radius: 6px;
        font-size: 0.875rem;
        color: #1e40af;
        line-height: 1.5;
      }

      .wcag-form-timeout-notice svg {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        color: #3b82f6;
      }

      .wcag-form-notice-text {
        flex: 1;
      }

      .wcag-form-notice-text strong {
        color: #1e3a8a;
        font-weight: 600;
      }

      /* Responsive */
      @media (max-width: 640px) {
        .wcag-timeout-policy-notice {
          padding: 0 15px;
          margin: 15px auto;
        }

        .wcag-policy-container {
          flex-direction: column;
          gap: 12px;
          padding: 16px;
        }

        .wcag-policy-icon {
          width: 32px;
          height: 32px;
        }

        .wcag-policy-title {
          font-size: 1rem;
        }

        .wcag-policy-text {
          font-size: 0.875rem;
        }

        .wcag-policy-dismiss {
          position: absolute;
          top: 12px;
          right: 12px;
        }
      }

      /* Dark mode support */
      @media (prefers-color-scheme: dark) {
        .wcag-policy-container {
          background: linear-gradient(135deg, #1e3a8a 0%, #312e81 100%);
          border-color: #60a5fa;
        }

        .wcag-policy-icon {
          color: #93c5fd;
        }

        .wcag-policy-title {
          color: #dbeafe;
        }

        .wcag-policy-text {
          color: #bfdbfe;
        }

        .wcag-policy-text strong {
          color: #dbeafe;
        }

        .wcag-policy-subtext {
          color: #93c5fd;
        }

        .wcag-policy-dismiss {
          background: rgba(255, 255, 255, 0.1);
          color: #dbeafe;
        }

        .wcag-policy-dismiss:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .wcag-form-timeout-notice {
          background-color: #1e3a8a;
          border-color: #3b82f6;
          color: #bfdbfe;
        }

        .wcag-form-notice-text strong {
          color: #dbeafe;
        }
      }

      /* Print */
      @media print {
        .wcag-timeout-policy-notice,
        .wcag-form-timeout-notice {
          display: none !important;
        }
      }

      /* Reduced motion */
      @media (prefers-reduced-motion: reduce) {
        .wcag-timeout-policy-notice,
        .wcag-policy-dismiss {
          transition: none;
        }
      }
    `;

    document.head.appendChild(style);
  }

  // Initialize ASAP (important for scanners)
  domReady(() => {
    addStyles();
    init();
  });

  // Re-scan for new forms when DOM changes
  const observer = new MutationObserver(() => {
    attachFormNotices();
  });

  if (document.body) {
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
})();
